import React, { useEffect, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  buyValue,
  sellValue,
  orderTypes
} from 'apex-web/lib/constants/sendOrder/orderEntryFormConstants';
import { getBEMClasses } from 'apex-web/lib/helpers/cssClassesHelper';
import { convertIncrementToIntDecimalPlaces } from 'apex-web/lib/helpers/decimalPlaces/decimalPlacesHelper';
import { biggerThanZero } from 'apex-web/lib/helpers/formValidations';
import { formatNumberToLocale } from 'apex-web/lib/helpers/numberFormatter';
import {
  getOrderBorrowingPower,
  getOrderLeverage,
  getSubmitButtonText
} from 'apex-web/lib/helpers/orderHelper';
import { instrumentPropType } from 'apex-web/lib/propTypes/instrumentPropTypes';
import { styleNames } from 'apex-web/lib/propTypes/commonComponent';
import ConfirmOrderModal from 'apex-web/lib/components/ConfirmOrderModal';
import APButton from 'apex-web/lib/components/common/APButton';
import APLabelWithText from 'apex-web/lib/components/common/APLabelWithText';
import APNumberInput from 'apex-web/lib/components/common/APNumberInput';
import APSegmentedButton from 'apex-web/lib/components/common/APSegmentedButton';
import BuySellTabs from 'apex-web/lib/components/OrderEntryComponents/BuySellTabs';
import 'apex-web/lib/styles/components/TradeComponent.css';
import 'apex-web/lib/components/OrderEntryComponents/OrderEntry.css';

import TimeInForceListboxField from './TimeInForceListboxField';

const baseClass = 'order-entry';
const baseClasses = getBEMClasses(baseClass);
const tradeComponentClasses = getBEMClasses('trade-component');

const percentOptions = [
  { value: 0.25, text: '25%' },
  { value: 0.5, text: '50%' },
  { value: 0.75, text: '75%' },
  { value: 1.0, text: '100%' }
];

const OrderEntryAnchoringComponent = (props, context) => {
  const {
    openAdvancedOrderSidePane,
    openReportBlockTradeSidePane,
    orderEntryForm,
    marketPrice,
    selectedInstrument,
    handleSubmit,
    submitting,
    fetching,
    invalid,
    disableTrading,
    VerificationRequiredComponent,
    hideFees,
    orderTotal,
    orderNet,
    orderFee,
    isMarginActive,
    marginProducts,
    title,
    allowBlockTrading,
    reduxFormChange,
    positions,
    level1
  } = props;

  const buy = orderEntryForm.values.side === buyValue;
  const sell = orderEntryForm.values.side === sellValue;
  const { anchoredField } = orderEntryForm.values;
  const price =
    orderEntryForm.values.orderType === orderTypes.limit.displayName
      ? orderEntryForm.values.limitPrice
      : orderEntryForm.values.orderType === orderTypes.stopMarket.displayName
        ? orderEntryForm.values.stopPrice
        : (level1 && (level1[buy ? 'BestOffer' : 'BestBid'])) || 0;
  const valueDP = convertIncrementToIntDecimalPlaces(
    selectedInstrument.PriceIncrement
  );
  const quantityDP = convertIncrementToIntDecimalPlaces(
    selectedInstrument.QuantityIncrement
  );
  const position =
    positions && selectedInstrument
      ? positions[buy ? selectedInstrument.Product2 : selectedInstrument.Product1]
      : null;
  const balance = position ? position.Amount - position.Hold : 0;
  const maxQuantity = buy ? balance : balance * price;

  useEffect(() => {
    if (anchoredField === 'value') {
      const quantity = (parseFloat(orderEntryForm.values.value) || 0) / price;
      if (typeof quantity === 'number' && !Number.isNaN(quantity) && Number.isFinite(quantity)) {
        reduxFormChange('quantity', quantity.toFixed(quantityDP));
      }
    }
  }, [anchoredField, price, orderEntryForm.values.value, quantityDP, reduxFormChange]);

  useEffect(() => {
    if (anchoredField === 'quantity') {
      const val = price * (parseFloat(orderEntryForm.values.quantity) || 0);
      if (typeof val === 'number' && !Number.isNaN(val)) {
        reduxFormChange('value', val.toFixed(valueDP));
      }
    }
  }, [anchoredField, price, orderEntryForm.values.quantity, valueDP, reduxFormChange]);

  const onMaxClicked = useCallback(
    e => {
      const percent = parseFloat(e.target.value) || 0;
      const amount = (percent * maxQuantity).toFixed(quantityDP);
      reduxFormChange('anchoredField', 'value');
      reduxFormChange('value', amount);
    },
    [maxQuantity, quantityDP, reduxFormChange]
  );

  const resetState = () => {
    props.change('limitPrice', '');
    props.change('stopPrice', '');
    props.untouch('limitPrice');
    props.untouch('stopPrice');
  };

  return (
    <div className={classNames(baseClasses())}>
      {title || null}
      <form onSubmit={handleSubmit} className={baseClasses('form')}>
        <BuySellTabs
          baseClass={baseClass}
          styleName={sell ? styleNames.subtractive : styleNames.additive}
        />
        <div className={baseClasses('body')}>
          <div className={baseClasses('order-type-wrapper')}>
            <APSegmentedButton
              name="orderType"
              label={context.t('Order Type')}
              items={[
                {
                  value: orderTypes.market.displayName,
                  text: context.t('Market'),
                  dataTest: 'Market Order Type',
                  title: context.t('Buy or sell at the best available price'),
                  onClick: resetState
                },
                {
                  value: orderTypes.limit.displayName,
                  text: context.t('Limit'),
                  dataTest: 'Limit Order Type',
                  title: context.t('Buy or sell at the specified price or better')
                },
                {
                  value: orderTypes.stopMarket.displayName,
                  text: context.t('Stop'),
                  dataTest: 'Stop Order Type',
                  title: context.t(
                    'Buy or sell once the price reaches a specified Stop Price'
                  )
                }
              ]}
              customClass={baseClass}
              styleName={buy ? styleNames.additive : styleNames.subtractive}
            />
          </div>
          {orderEntryForm.values.orderType === orderTypes.limit.displayName && (
            <APNumberInput
              type="text"
              name="limitPrice"
              placeholder="0"
              customClass={baseClass}
              label={context.t('Limit Price')}
              labelInInput={selectedInstrument && selectedInstrument.Product2Symbol}
              validate={[biggerThanZero]}
              decimalPlaces={valueDP}
              step={selectedInstrument.PriceIncrement}
              min={0}
            />
          )}
          {orderEntryForm.values.orderType ===
            orderTypes.stopMarket.displayName && (
            <APNumberInput
              type="text"
              name="stopPrice"
              placeholder="0"
              customClass={baseClass}
              label={context.t('Stop Price')}
              labelInInput={
                selectedInstrument
                  ? context.t(''.concat(selectedInstrument.Product2Symbol, ':'))
                  : ''
              }
              validate={[biggerThanZero]}
              decimalPlaces={valueDP}
              step={selectedInstrument.PriceIncrement}
              min={0}
            />
          )}
          <APNumberInput
            name="quantity"
            placeholder="0"
            customClass={baseClass}
            label={buy ? context.t('Buy Amount') : context.t('Sell Amount')}
            labelInInput={selectedInstrument.Product1Symbol}
            validate={[biggerThanZero]}
            decimalPlaces={quantityDP}
            step={selectedInstrument.QuantityIncrement}
            onFocus={() => reduxFormChange('anchoredField', 'quantity')}
            anchored={anchoredField === 'quantity'}
            min={0}
          />
          <APNumberInput
            name="value"
            placeholder="0"
            customClass={baseClass}
            label={context.t('Order Total')}
            labelInInput={selectedInstrument.Product2Symbol}
            validate={[biggerThanZero]}
            decimalPlaces={valueDP}
            step={selectedInstrument.PriceIncrement}
            onFocus={() => reduxFormChange('anchoredField', 'value')}
            anchored={anchoredField === 'value'}
            min={0}
          />
          <div
            className={`${baseClasses('input-wrapper')} ${baseClass}-percent`}>
            {percentOptions.map(option => (
              <button
                key={option.text}
                type="button"
                value={option.value}
                onClick={onMaxClicked}
              >
                {option.text}
              </button>
            ))}
          </div>
          {orderEntryForm.values.orderType === orderTypes.limit.displayName && (
            <TimeInForceListboxField
              name="timeInForce"
              label={context.t('Time in Force')}
              customClass={baseClass}
              options={[
                { value: 1, label: context.t('Good Til Canceled') },
                { value: 3, label: context.t('Immediate or Cancel') },
                { value: 4, label: context.t('Fill or Kill') }
              ]}
            />
          )}
          <div className={tradeComponentClasses('section')}>
            {isMarginActive && (
              <React.Fragment>
                <APLabelWithText
                  name="borrowingPower"
                  label={context.t('Borrowing Power')}
                  text={`${
                    buy
                      ? selectedInstrument.Product2Symbol
                      : selectedInstrument.Product1Symbol
                  } ${formatNumberToLocale(
                    getOrderBorrowingPower(
                      orderEntryForm,
                      selectedInstrument,
                      marginProducts
                    ),
                    buy ? valueDP : quantityDP
                  )}`}
                  customClass={baseClass}
                />
                <APLabelWithText
                  name="leverage"
                  label={context.t('Leverage')}
                  text={getOrderLeverage(orderEntryForm)}
                  customClass={baseClass}
                />
              </React.Fragment>
            )}
            <APLabelWithText
              name="marketPrice"
              label={context.t('Market Price')}
              text={`${selectedInstrument.Product2Symbol} ${marketPrice}`}
              customClass={baseClass}
            />
            {!hideFees && (
              <APLabelWithText
                label={context.t('Fees')}
                customClass={baseClass}
                text={orderFee}
              />
            )}
            <APLabelWithText
              label={context.t('Order Total')}
              text={orderTotal}
              customClass={baseClass}
            />
            {!hideFees && (
              <APLabelWithText
                label={context.t('Net')}
                text={orderNet}
                customClass={baseClass}
              />
            )}
          </div>
          <div className={tradeComponentClasses('section')}>
            <APButton
              type="submit"
              disabled={submitting || fetching || invalid || disableTrading}
              customClass={baseClass}
              styleName={buy ? styleNames.additive : styleNames.subtractive}
            >
              {submitting
                ? context.t('Processing...')
                : context.t(getSubmitButtonText(orderEntryForm))}
            </APButton>
            {VerificationRequiredComponent}
          </div>
          <div className={tradeComponentClasses('section')}>
            <div
              className={classNames(baseClasses('item-button'))}
              onClick={openAdvancedOrderSidePane}
            >
              {`« ${context.t('Advanced Orders')}`}
            </div>
          </div>
          {allowBlockTrading && (
            <div className={tradeComponentClasses('section')}>
              <div
                className={classNames(baseClasses('item-button'))}
                onClick={openReportBlockTradeSidePane}
              >
                {`« ${context.t('Report Block Trade')}`}
              </div>
            </div>
          )}
        </div>
      </form>
      <ConfirmOrderModal />
    </div>
  );
};

OrderEntryAnchoringComponent.defaultProps = {
  submitting: false,
  isMarginActive: false,
  allowBlockTrading: false
};

OrderEntryAnchoringComponent.propTypes = {
  change: PropTypes.func.isRequired,
  untouch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  allowBlockTrading: PropTypes.bool,
  openAdvancedOrderSidePane: PropTypes.func.isRequired,
  openReportBlockTradeSidePane: PropTypes.func.isRequired,
  selectedInstrument: instrumentPropType,
  orderEntryForm: PropTypes.object.isRequired,
  marketPrice: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  fetching: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  disableTrading: PropTypes.bool.isRequired,
  VerificationRequiredComponent: PropTypes.element
};

OrderEntryAnchoringComponent.contextTypes = {
  t: PropTypes.func.isRequired
};

export default OrderEntryAnchoringComponent;
